import { CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, List, ListItem, ListProps, SingleSelectDropdown, Tooltip, TooltipPosition } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { RunFlowTaskInfo } from '~src/flow/models/RunFlowTaskInfo';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { classes, cssClass } from '~src/style';

import { FlowParameterNameAndValue } from '../FlowDataGridDefinitions';
import { getConnectionCategory, getOutputStepIconFromConnectionCategory } from '../utils/FlowDataConnectionUtils';
import { areFlowParametersEnabled } from '../utils/FlowParametersUtils';
import { isFlowSelfServiceSchedulesEnabled } from '../utils/FlowSelfServiceSchedulesUtils';

const rootClass: string = 'FlowSubscriptionRunFlowTaskDropdown';

const infoIconImgPath = imgPath('info_14px_small.svg');

const runFlowTaskRowClass = cssClass(rootClass, 'runFlowTaskRow', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
});

const infoIcon = cssClass(rootClass, 'infoIcon', {
  height: '14px',
  marginLeft: '6px',
});

const outputNameCellIcon = cssClass(rootClass, 'outputNameCellIcon', {
  flex: '0 0 auto',
  height: '18px',
});

const outputClass = cssClass(rootClass, 'output', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  paddingBottom: '3px'
});

const tooltipTitle = cssClass(rootClass, 'tooltipTitle', {
  paddingBottom: '8px',
  fontWeight: 'bold',
});

const countHeader = cssClass(rootClass, 'countHeader', {
  padding: '8px 0',
  fontWeight: 600,
});

const scheduleDropdownItem = cssClass(rootClass, 'scheduleDropdownItem', {
  maxWidth: 500,
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

export type FlowSubscriptionRunFlowTaskDropdownProps = {
  readonly runFlowTasks: RunFlowTaskInfo[];
  readonly value: string | null;
  readonly isFetchingData: boolean;
  readonly onChange: (value: string) => void;
  readonly flowName: string;
}

export const FlowSubscriptionRunFlowTaskDropdown: React.FC<FlowSubscriptionRunFlowTaskDropdownProps> = (props: FlowSubscriptionRunFlowTaskDropdownProps): JSX.Element => {
  if (props.runFlowTasks === null) {
    return;
  }

  function getOutputRow(runFlowTask: RunFlowTaskInfo): React.ReactNode {
    const renderOutputs = (): JSX.Element[] => {
      return runFlowTask.flowOutputSteps.map((flowOutputStep, index) => {
        return (
          <div className={outputClass} key={index}>
            <div className={classes(outputNameCellIcon, getOutputStepIconFromConnectionCategory(getConnectionCategory(flowOutputStep.dataConnection)))}/>
            <span>
              {runFlowTask.isRunAllIncremental ?
                Messages.flowSubscriptionsOutputNameIncremental({OUTPUT_NAME: flowOutputStep.name})
                : Messages.flowSubscriptionsOutputNameFull({OUTPUT_NAME: flowOutputStep.name})
              }
            </span>
          </div>
        );
      });
    };

    const renderParametersSection = (): JSX.Element => {
      return areFlowParametersEnabled() ? (
        <>
          <div className={countHeader}>
            {Messages.flowParametersCount(
              {COUNT: runFlowTask.relevantParameterRuns.length, COUNT_LOC: Formatters.formatCount(runFlowTask.relevantParameterRuns.length)}
            )}
          </div>
          {renderParameters()}
        </>
      ) : null;
    };

    const renderParameters = (): JSX.Element[] => {
      return runFlowTask.relevantParameterRuns.map(parameter => {
        return (
          <div className={outputClass} key={parameter.parameterId}>
            <FlowParameterNameAndValue
              name={parameter.name}
              id={parameter.parameterId}
              value={parameter.overrideValue}
              type={parameter.domain.type}
            />
          </div>
        );
      });
    };

    const getTooltipContent = (): JSX.Element => {
      const isLinkedTask: boolean = runFlowTask && runFlowTask.linkedTaskId !== null;
      return (
        <div>
          <div className={tooltipTitle}>
            {isLinkedTask ?
              Messages.flowSubscriptionsLinkedTaskStepNumber({
                FLOW_NAME: props.flowName,
                STEP_NUMBER_LOC: Formatters.formatCount(runFlowTask.stepNumber),
                TOTAL_STEPS_LOC: Formatters.formatCount(runFlowTask.totalSteps)
                })
            : props.flowName}
          </div>
          <div className={countHeader}>
            {Messages.outputsCount(
              {COUNT: runFlowTask.flowOutputSteps.length, COUNT_LOC: Formatters.formatCount(runFlowTask.flowOutputSteps.length)}
            )}
          </div>
          {renderOutputs()}
          {renderParametersSection()}
        </div>
      );
    };

    const scheduleName: string = isFlowSelfServiceSchedulesEnabled() ?
      IScheduleInfo.getScheduleDescription(runFlowTask.schedule) :
      runFlowTask.schedule.name;

    return (
      <div className={runFlowTaskRowClass} >
        <span className={scheduleDropdownItem} title={scheduleName}>{scheduleName}</span>
        <Tooltip
          getContent={getTooltipContent}
          position={TooltipPosition.BottomRightCorner}
          testId={'flow-subscription-task-tooltip'}
        >
          <img className={infoIcon} src={infoIconImgPath} alt={''}/>
        </Tooltip>
      </div>
    );
  }

  const getList = (props: FlowSubscriptionRunFlowTaskDropdownProps): JSX.Element => {
    const items: ListItem[] = [];

    props.runFlowTasks.forEach((runFlowTask) => {
        items.push({value: runFlowTask.id, content: getOutputRow(runFlowTask)});
    });

    const listProps: ListProps = {items: items};
    return <List {...listProps} />;
  };

  const list: JSX.Element = getList(props);

  const flowHasNoRunFlowTasks: boolean = !props.isFetchingData && props.runFlowTasks.length < 1;

  return (
    <>
      <SingleSelectDropdown
        label={Messages.flowSubscriptionsTaskDropdownLabel()}
        type={DropdownButtonType.Outline}
        list={list}
        value={props.value}
        onChange={props.onChange}
        testId={'flow-subscription-run-flow-task-dropdown'}
        disabled={props.isFetchingData || flowHasNoRunFlowTasks}
      >
        {props.value === null ? Messages.flowSubscriptionsTaskDropdownPlaceholder() : null}
      </SingleSelectDropdown>
      { flowHasNoRunFlowTasks &&
      <CompactInlineAlertWidget
        testId={'no-schedules-warning'}
        type={InlineAlertType.Error}
        style={{marginTop: '12px'}}
      >
        {Messages.flowSubscriptionsNoSchedulesExist()}
      </CompactInlineAlertWidget>
      }
    </>
  );
};
